import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { WalletService } from '../../../../services/wallet/wallet.service';
import { CurrencyPipe } from '@angular/common';
import { HasRoleDirectiveDirective } from '../../../../common/directives/has-role/has-role-directive.directive';
import { FormsModule } from '@angular/forms';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { VoucherService } from '../../../../services/voucher/voucher.service';
import { Subscription, take } from 'rxjs';

@Component({
  selector: 'wlt-top-up-drawer',
  standalone: true,
  imports: [CurrencyPipe, HasRoleDirectiveDirective, FormsModule],
  templateUrl: './top-up-drawer.component.html',
  styleUrl: './top-up-drawer.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TopUpDrawerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('drawerDialog', { static: false }) drawerDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('openDrawerButton', { static: false }) openDrawerButton?: ElementRef<HTMLButtonElement>;


  /** TopUp Request amount */
  customTopUpRequest: number | undefined;
  /** Custom TopUp amaount */
  customTopUp: number | undefined;

  voucherName = signal('');
  redeemingVoucher = signal(false);

  private dialogCloseHandler?: () => void;
  private subscriptionList = new Subscription();

  /** Services */
  public walletService = inject(WalletService);
  public currentUserService = inject(CurrentUserService);
  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);
  private firebaseService = inject(FirebaseService);
  private voucherService = inject(VoucherService);
  /** Services - END */

  ngOnInit(): void {
    console.log('TopUpDrawerComponent ngOnInit');
    const target = this.walletService.topUpTarget();
    if (target?.businessAccountId) {
      this.subscriptionList.add(
        this.walletService.getBalance(target.businessAccountId).subscribe({
          next: (balance) => {
            if (!target.currencyCode && balance.currencyCode) {
              this.walletService.topUpTarget.update((currentTarget) =>
                currentTarget?.businessAccountId === target.businessAccountId
                  ? { ...currentTarget, currencyCode: balance.currencyCode }
                  : currentTarget
              );
            }
          },
          error: (err) => console.error('Failed to load top-up target balance:', err),
        }),
      );
    }
  }

  closeDrawer() {
    this.walletService.closeTopUpDrawer();
  }

  ngAfterViewInit(): void {
    if (this.openDrawerButton?.nativeElement) {
      setTimeout(() => {
        this.openDrawerButton?.nativeElement.click();
      }, 0);
    }

    if (this.drawerDialog?.nativeElement) {
      this.dialogCloseHandler = () => {
        this.closeDrawer();
      };
      this.drawerDialog.nativeElement.addEventListener('close', this.dialogCloseHandler);
    }
  }


  async topupRequest() {
    if (this.customTopUpRequest) {
      if (this.customTopUpRequest < 100) {

        const confirm = await this.alertService.confirm({
          title: `Whoops!`,
          message: `Minimum Top-Up value is £100.`,
          confirmText: `Thanks`,
          cancelText: 'Cancel',
          type: 'alert',
          icon: 'info'
        });
        if (confirm) { }
      } else {
        const businessAccountId = this.getTopUpBusinessAccountId();
        if (!businessAccountId) {
          this.showMissingTopUpAccountError();
          return;
        }
        this.subscriptionList.add(
          this.walletService.walletTopupRequest(businessAccountId, this.customTopUpRequest * 100).subscribe(async (requestRes) => {

            this.toastNotificationService.alert({
              title: `Request Sent!`,
              message: `You have requested ${this.getTopUpCurrencySymbol()}${this.customTopUpRequest} to be added to ${this.getTopUpCompanyName()}s wallet`,
              type: 'success',
              icon: 'success'
            });

            this.closeDrawer();
          }, async (requestErr) => {
            console.warn('requestError', requestErr);

            const confirm = await this.alertService.confirm({
              title: `Request Not Sent!`,
              message: `Something has gone wrong`,
              confirmText: `Ok`,
              cancelText: 'Cancel',
              type: 'alert',
              icon: 'error'
            });
            if (confirm) { }
          }),
        );
      }
    }
  }

  async customDeposit() {
    if (this.customTopUp) {
      if (this.customTopUp > 99) {
        this.topUp(this.customTopUp * 100);
      } else {

        const confirm = await this.alertService.confirm({
          title: 'Whoops!',
          message: `Minimum Top-Up value is £100.`,
          icon: 'info',
          confirmText: 'Thanks',
          type: 'alert',
          cancelText: 'Cancel',
        });
      }
    }
  }


  onVoucherNameInput(event: Event): void {
    this.voucherName.set((event.currentTarget as HTMLInputElement).value);
  }

  redeemVoucher(event: Event): void {
    event.preventDefault();
    const name = this.voucherName().trim();
    const businessAccountId = this.getTopUpBusinessAccountId();
    if (!name || !businessAccountId || this.redeemingVoucher()) {
      return;
    }

    this.redeemingVoucher.set(true);
    this.subscriptionList.add(
      this.voucherService.redeemByName({ businessAccountId, name }).pipe(take(1)).subscribe({
        next: () => {
          this.redeemingVoucher.set(false);
          this.voucherName.set('');
          this.toastNotificationService.alert({
            title: 'Voucher redeemed',
            message: 'The credit has been added to your wallet.',
            type: 'success',
            icon: 'success',
          });
          this.subscriptionList.add(
            this.walletService.getBalance(businessAccountId).subscribe({
              error: (err) => console.error('Failed to refresh balance after voucher redeem:', err),
            }),
          );
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.redeemingVoucher.set(false);
          this.toastNotificationService.alert({
            title: 'Voucher not redeemed',
            message: TopUpDrawerComponent.voucherErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  topUpWithAmount(amount: number) {
    this.topUp(amount * 100);
  }

  topUp(amount: number) {
    if (amount >= 10000) {
      this.stripeTopUp(amount);
    }
  }

  stripeTopUp(amount: number) {
    if (this.firebaseService.user$) { 
      const redirectUrl = `${window.location.href}`;
      const businessAccountId = this.getTopUpBusinessAccountId();
      const currencyCode = this.getTopUpCurrencyCode();

      if (!businessAccountId || !currencyCode) {
        this.showMissingTopUpAccountError();
        return;
      }

      this.subscriptionList.add(
        this.walletService.topUpLinkStripe(amount, currencyCode, this.firebaseService.userId ?? '',
          businessAccountId, redirectUrl).subscribe((topUp: any) => {
            // this.router.navigate(`${environment.acquired.url}${topUp.linkId}`);
            console.log('👮🏻‍♂️👮🏻‍♂️ Top Up:', topUp);
            window.location.href = topUp.url;
          }),
      );
    }
  }

  private getTopUpBusinessAccountId(): string | undefined {
    return this.walletService.topUpTarget()?.businessAccountId ?? this.currentUserService.focussedBrand?.id;
  }

  getTopUpCurrencyCode(): string | undefined {
    const target = this.walletService.topUpTarget();
    if (target) {
      if (target.currencyCode) {
        return target.currencyCode;
      }

      return this.walletService.balanceBusinessAccountId() === target.businessAccountId
        ? this.walletService.currencyCode()
        : undefined;
    }

    return this.currentUserService.focussedBrand?.currencyCode ?? this.walletService.currencyCode();
  }

  getTopUpCurrencySymbol(): string {
    switch (this.getTopUpCurrencyCode()?.toUpperCase()) {
      case 'USD':
        return '$';
      case 'EUR':
        return '\u20ac';
      default:
        return '£';
    }
  }

  private getTopUpCompanyName(): string {
    return this.walletService.topUpTarget()?.companyName ?? this.currentUserService.focussedBrand?.companyName ?? 'your';
  }

  private static voucherErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const message =
      err?.error?.message || err?.message || 'That voucher was not found or is no longer active.';
    return Array.isArray(message) ? message.join(', ') : String(message);
  }

  private showMissingTopUpAccountError(): void {
    this.toastNotificationService.alert({
      title: 'Top-up unavailable',
      message: 'We could not identify which wallet to top up. Please refresh and try again.',
      type: 'error',
      icon: 'error',
    });
  }

  

  ngOnDestroy(): void {
    console.log('TopUpDrawerComponent ngOnDestroy');
    this.subscriptionList.unsubscribe();
    if (this.dialogCloseHandler && this.drawerDialog?.nativeElement) {
      this.drawerDialog.nativeElement.removeEventListener('close', this.dialogCloseHandler);
    }
    if (this.walletService.topUpDrawerOpen()) {
      this.walletService.closeTopUpDrawer();
    }
  }
}
